import { action } from "typesafe-actions";
import { IRsaPackage } from "./rsaPackages.types";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import { api } from "../../api/api";
import {
  closeLoaderWithMessage,
  openLoaderWithMessage,
  showMessage,
} from "../messages/messagesActions";
import { ISearchQueryParams } from "../common/common.types";
import { addSearchFilterToAdvanceFilter, getSearchQueryV2 } from "../common/helpers";
import { endPoints } from "../../constants/endPoints";

/* ================= LIST ACTIONS ================= */

export const FETCH_RSA_PACKAGE_LIST_PROGRESS = "FETCH_RSA_PACKAGE_LIST_PROGRESS";
export const FETCH_RSA_PACKAGE_LIST_SUCCESS = "FETCH_RSA_PACKAGE_LIST_SUCCESS";
export const FETCH_RSA_PACKAGE_LIST_FAILED = "FETCH_RSA_PACKAGE_LIST_FAILED";

export const fetchRsaPackageListProgress = () =>
  action(FETCH_RSA_PACKAGE_LIST_PROGRESS);

export const fetchRsaPackageListSuccess = (
  list: IRsaPackage[],
  totalRecords: number
) => action(FETCH_RSA_PACKAGE_LIST_SUCCESS, { list, totalRecords });

export const fetchRsaPackageListFailed = () =>
  action(FETCH_RSA_PACKAGE_LIST_FAILED);

export const fetchRsaPackageListAsync = (
  queryParams: ISearchQueryParams
): ThunkAction<void, IStoreState, {}, AnyAction> => async (dispatch) => {
  try {
    dispatch(fetchRsaPackageListProgress());

    const {
      page,
      rowsPerPage,
      status,
      date,
      advancedFilters,
      package_name,
      vehicle_type,
      partner_name,
      companyOutletUuid
    } = queryParams as any;

    const advanceFilter: any[] = Array.isArray(advancedFilters) ? [...advancedFilters] : [];

    addSearchFilterToAdvanceFilter(advanceFilter, ["package_name"], package_name);
    addSearchFilterToAdvanceFilter(advanceFilter, ["vehicle_type"], vehicle_type);
    addSearchFilterToAdvanceFilter(advanceFilter, ["partner_name"], partner_name);

    let finalUrl = `${endPoints.packages.getRsaPackages}?pageNo=${page}&itemPerPage=${rowsPerPage}`;

    if (date?.fromDate && date?.toDate) {
      finalUrl += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
    }

    if (status && status !== "-1") {
      finalUrl += `&status=${status}`;
    }

    if (companyOutletUuid) {
        finalUrl += `&company_outlet_uuid=${companyOutletUuid}`;
    }

    if (advanceFilter.length > 0) {
      finalUrl += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
    }

    const res = await api.get(finalUrl);

    const data: IRsaPackage[] = res.data.data || [];
    const totalRecords = res.data.totalRecords || 0;

    dispatch(fetchRsaPackageListSuccess(data, totalRecords));
  } catch (err: any) {
    dispatch(fetchRsaPackageListFailed());
    dispatch(
      showMessage({
        message: err?.response?.data?.message || "Failed to fetch RSA packages",
        type: "error",
        displayAs: "snackbar",
      })
    );
  }
};

/* ================= SINGLE ACTIONS ================= */

export const FETCH_RSA_PACKAGE_PROGRESS = "FETCH_RSA_PACKAGE_PROGRESS";
export const FETCH_RSA_PACKAGE_SUCCESS = "FETCH_RSA_PACKAGE_SUCCESS";
export const FETCH_RSA_PACKAGE_FAILED = "FETCH_RSA_PACKAGE_FAILED";

export const fetchRsaPackageProgress = () =>
  action(FETCH_RSA_PACKAGE_PROGRESS);

export const fetchRsaPackageSuccess = (rsaPackage: IRsaPackage) =>
  action(FETCH_RSA_PACKAGE_SUCCESS, { rsaPackage });

export const fetchRsaPackageFailed = (errorMessage: string) =>
  action(FETCH_RSA_PACKAGE_FAILED, { errorMessage });

export const fetchRsaPackageAsync =
  (packageUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(fetchRsaPackageProgress());

        const res = await api.get(
          `${endPoints.packages.getRsaPackages}?rsa_package_master_uuid=${packageUuid}`
        );

        const data: IRsaPackage = res.data.data[0];

        dispatch(fetchRsaPackageSuccess(data));
      } catch (err: any) {
        dispatch(
          fetchRsaPackageFailed(
            err?.response?.data?.message ||
            "Failed to fetch RSA package"
          )
        );
        dispatch(
          showMessage({
            message:
              err?.response?.data?.message ||
              "Failed to fetch RSA package",
            type: "error",
            displayAs: "snackbar",
          })
        );
      }
    };

/* ================= UPSERT ACTION ================= */

export const upsertRsaPackageAsync =
  (
    rsaPackageData: IRsaPackage,
    onCallBack: (isSuccess?: boolean, data?: IRsaPackage) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(openLoaderWithMessage("Saving RSA package, please wait..."));

        const {
          create_ts,
          insert_ts,
          created_by_name,
          created_by_uuid,
          modified_by_name,
          modified_by_uuid,
          ...payload
        } = rsaPackageData;

        const res = await api.post(endPoints.packages.upsertRsaPackages, payload);

        const data: IRsaPackage = res.data.data;

        dispatch(
          showMessage({
            message: res.data.message,
            type: "success",
            displayAs: "snackbar",
          })
        );

        onCallBack(true, data);
      } catch (err: any) {
        dispatch(
          showMessage({
            message:
              err?.response?.data?.message ||
              "Failed to save RSA package",
            type: "error",
            displayAs: "snackbar",
          })
        );
        onCallBack(false);
      } finally {
        dispatch(closeLoaderWithMessage());
      }
    };

/* ================= CLEAR ================= */

export const CLEAR_RSA_PACKAGE_STATE = "CLEAR_RSA_PACKAGE_STATE";
export const CLEAR_RSA_PACKAGE = "CLEAR_RSA_PACKAGE";

export const clearRsaPackageState = () =>
  action(CLEAR_RSA_PACKAGE_STATE);

export const clearRsaPackage = () =>
  action(CLEAR_RSA_PACKAGE);
